import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { 
  Form, 
  FormControl, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormMessage 
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Box, Upload, Check } from "lucide-react";

type FormData = {
  productName: string;
  sku: string;
  barcode: string;
  category: string;
  subcategory: string;
  brand: string;
  packSize: string;
  purchasePrice: string; // Changed to string for better decimal handling
  sellingPriceMode: "POS" | "JimatShop" | "Both";
  sellingPricePOS: string; // Changed to string for better decimal handling
  sellingPriceJimatShop: string; // Changed to string for better decimal handling
  tax: boolean;
  suppliers: string[];
  channels: string[];
  lowStockThreshold: string; // Added low stock threshold field
};

const categories = [
  "Beverages",
  "Bakery",
  "Canned Goods",
  "Dairy",
  "Fruits",
  "Vegetables",
  "Snacks",
  "Household"
];

const subcategories = {
  "Beverages": ["Soft Drinks", "Juices", "Coffee", "Tea", "Water"],
  "Bakery": ["Bread", "Cakes", "Pastries", "Cookies"],
  "Canned Goods": ["Vegetables", "Fruits", "Meat", "Fish", "Soup"],
  "Dairy": ["Milk", "Cheese", "Yogurt", "Butter", "Cream"],
  "Fruits": ["Fresh", "Dried", "Frozen"],
  "Vegetables": ["Fresh", "Frozen", "Processed"],
  "Snacks": ["Chips", "Nuts", "Chocolates", "Candy", "Crackers"],
  "Household": ["Cleaning", "Paper Goods", "Laundry", "Kitchen"]
};

const suppliers = [
  "Fresh Farm",
  "Organic Supplies",
  "Local Bakery",
  "Snack Distributors",
  "Beverage Wholesale",
  "Food Supplies",
  "Sweet Wholesale",
];

const AddNewProductTab: React.FC = () => {
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [availableSubcategories, setAvailableSubcategories] = useState<string[]>([]);
  
  const form = useForm<FormData>({
    defaultValues: {
      productName: "",
      sku: "",
      barcode: "",
      category: "",
      subcategory: "",
      brand: "",
      packSize: "",
      purchasePrice: "",
      sellingPriceMode: "POS",
      sellingPricePOS: "",
      sellingPriceJimatShop: "",
      tax: false,
      suppliers: [],
      channels: ["POS"],
      lowStockThreshold: "10", // Default low stock threshold
    },
  });

  const { watch, setValue } = form;
  const purchasePrice = watch("purchasePrice");
  const sellingPricePOS = watch("sellingPricePOS");
  const sellingPriceJimatShop = watch("sellingPriceJimatShop");
  const sellingPriceMode = watch("sellingPriceMode");
  const category = watch("category");
  
  // Update subcategories when category changes
  React.useEffect(() => {
    if (category && subcategories[category]) {
      setAvailableSubcategories(subcategories[category]);
      setValue("subcategory", "");
    } else {
      setAvailableSubcategories([]);
    }
  }, [category, setValue]);

  // Calculate margin for POS
  const calculatePOSMargin = () => {
    const purchasePriceNum = parseFloat(purchasePrice) || 0;
    const sellingPricePOSNum = parseFloat(sellingPricePOS) || 0;
    
    if (purchasePriceNum > 0 && sellingPricePOSNum > 0) {
      return ((sellingPricePOSNum - purchasePriceNum) / sellingPricePOSNum * 100).toFixed(2);
    }
    return "0.00";
  };

  // Calculate margin for JimatShop
  const calculateJimatShopMargin = () => {
    const purchasePriceNum = parseFloat(purchasePrice) || 0;
    const sellingPriceJimatShopNum = parseFloat(sellingPriceJimatShop) || 0;
    
    if (purchasePriceNum > 0 && sellingPriceJimatShopNum > 0) {
      return ((sellingPriceJimatShopNum - purchasePriceNum) / sellingPriceJimatShopNum * 100).toFixed(2);
    }
    return "0.00";
  };

  // Auto-sync prices when "Both" is selected
  React.useEffect(() => {
    if (sellingPriceMode === "Both") {
      if (sellingPricePOS && !sellingPriceJimatShop) {
        setValue("sellingPriceJimatShop", sellingPricePOS);
      } else if (sellingPriceJimatShop && !sellingPricePOS) {
        setValue("sellingPricePOS", sellingPriceJimatShop);
      }
    }
  }, [sellingPriceMode, sellingPricePOS, sellingPriceJimatShop, setValue]);

  const generateSKU = () => {
    const productName = form.getValues("productName");
    const category = form.getValues("category");
    
    if (productName && category) {
      const catPrefix = category.substring(0, 4).toUpperCase();
      const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
      setValue("sku", `${catPrefix}-${random}`);
    }
  };

  const generateBarcode = () => {
    // Generate a random 13-digit barcode
    const randomBarcode = Array.from({ length: 13 }, () => Math.floor(Math.random() * 10)).join("");
    setValue("barcode", randomBarcode);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const onSubmit = (data: FormData) => {
    // Convert string price values to numbers for submission
    const submissionData = {
      ...data,
      purchasePrice: parseFloat(data.purchasePrice) || 0,
      sellingPricePOS: parseFloat(data.sellingPricePOS) || 0,
      sellingPriceJimatShop: parseFloat(data.sellingPriceJimatShop) || 0,
      lowStockThreshold: parseInt(data.lowStockThreshold) || 10,
    };
    
    console.log("Form submitted:", submissionData);
    // Here you would typically save the new product
  };

  return (
    <div>
      <h2 className="text-xl font-semibold mb-4">Add New Product</h2>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-6">
              {/* Product Name */}
              <FormField
                control={form.control}
                name="productName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Product Name *</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter product name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* SKU */}
              <div className="flex gap-4 items-end">
                <FormField
                  control={form.control}
                  name="sku"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel>SKU</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter SKU" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="button" variant="outline" onClick={generateSKU} className="mb-0.5">
                  Generate
                </Button>
              </div>

              {/* Barcode */}
              <div className="flex gap-4 items-end">
                <FormField
                  control={form.control}
                  name="barcode"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel>Barcode</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter barcode" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="button" variant="outline" onClick={generateBarcode} className="mb-0.5">
                  Generate
                </Button>
              </div>

              {/* Category */}
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {categories.map(category => (
                          <SelectItem key={category} value={category}>
                            {category}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Subcategory */}
              <FormField
                control={form.control}
                name="subcategory"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Subcategory</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value} disabled={availableSubcategories.length === 0}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={availableSubcategories.length > 0 ? "Select subcategory" : "Select a category first"} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {availableSubcategories.map(subcategory => (
                          <SelectItem key={subcategory} value={subcategory}>
                            {subcategory}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Brand */}
              <FormField
                control={form.control}
                name="brand"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Brand</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter brand" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Pack Size / Unit */}
              <FormField
                control={form.control}
                name="packSize"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Pack Size / Unit</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., 500ml, 1kg" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Low Stock Threshold - New field */}
              <FormField
                control={form.control}
                name="lowStockThreshold"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Low Stock Threshold</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        min="1" 
                        placeholder="10"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                    <p className="text-xs text-muted-foreground">
                      Products with stock below this value will be marked as "Low Stock"
                    </p>
                  </FormItem>
                )}
              />
            </div>

            <div className="space-y-6">
              {/* Purchase Price */}
              <FormField
                control={form.control}
                name="purchasePrice"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Purchase Price</FormLabel>
                    <FormControl>
                      <Input 
                        type="text"
                        inputMode="decimal" 
                        placeholder="0.00"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Selling Price Mode */}
              <FormField
                control={form.control}
                name="sellingPriceMode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Selling Price Type</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="POS">POS Only</SelectItem>
                        <SelectItem value="JimatShop">JimatShop Only</SelectItem>
                        <SelectItem value="Both">POS & JimatShop</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Selling Price (POS) */}
              {(sellingPriceMode === "POS" || sellingPriceMode === "Both") && (
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="sellingPricePOS"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Selling Price (POS)</FormLabel>
                        <FormControl>
                          <Input 
                            type="text"
                            inputMode="decimal" 
                            placeholder="0.00"
                            {...field}
                            onChange={(e) => {
                              field.onChange(e.target.value);
                              if (sellingPriceMode === "Both") {
                                setValue("sellingPriceJimatShop", e.target.value);
                              }
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  {/* POS Margin (auto-calculated) */}
                  <div>
                    <FormLabel>POS Margin (%)</FormLabel>
                    <div className="h-10 rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 md:text-sm">
                      {calculatePOSMargin()}%
                    </div>
                  </div>
                </div>
              )}

              {/* Selling Price (JimatShop) */}
              {(sellingPriceMode === "JimatShop" || sellingPriceMode === "Both") && (
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="sellingPriceJimatShop"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Selling Price (JimatShop)</FormLabel>
                        <FormControl>
                          <Input 
                            type="text"
                            inputMode="decimal" 
                            placeholder="0.00"
                            {...field}
                            onChange={(e) => {
                              field.onChange(e.target.value);
                              if (sellingPriceMode === "Both") {
                                setValue("sellingPricePOS", e.target.value);
                              }
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  {/* JimatShop Margin (auto-calculated) */}
                  <div>
                    <FormLabel>JimatShop Margin (%)</FormLabel>
                    <div className="h-10 rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 md:text-sm">
                      {calculateJimatShopMargin()}%
                    </div>
                  </div>
                </div>
              )}

              {/* Available Channels */}
              <FormField
                control={form.control}
                name="channels"
                render={() => (
                  <FormItem>
                    <div className="mb-2">
                      <FormLabel>Available Channels</FormLabel>
                    </div>
                    <div className="flex space-x-4">
                      <FormField
                        control={form.control}
                        name="channels"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value?.includes("POS")}
                                onCheckedChange={(checked) => {
                                  const currentValue = [...(field.value || [])];
                                  if (checked) {
                                    if (!currentValue.includes("POS")) {
                                      currentValue.push("POS");
                                    }
                                  } else {
                                    const index = currentValue.indexOf("POS");
                                    if (index !== -1) {
                                      currentValue.splice(index, 1);
                                    }
                                  }
                                  field.onChange(currentValue);
                                }}
                              />
                            </FormControl>
                            <FormLabel className="mt-0">POS</FormLabel>
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="channels"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value?.includes("JimatShop")}
                                onCheckedChange={(checked) => {
                                  const currentValue = [...(field.value || [])];
                                  if (checked) {
                                    if (!currentValue.includes("JimatShop")) {
                                      currentValue.push("JimatShop");
                                    }
                                  } else {
                                    const index = currentValue.indexOf("JimatShop");
                                    if (index !== -1) {
                                      currentValue.splice(index, 1);
                                    }
                                  }
                                  field.onChange(currentValue);
                                }}
                              />
                            </FormControl>
                            <FormLabel className="mt-0">JimatShop</FormLabel>
                          </FormItem>
                        )}
                      />
                    </div>
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* Suppliers */}
          <div>
            <FormLabel className="block mb-2">Suppliers</FormLabel>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {suppliers.map((supplier) => (
                <FormField
                  key={supplier}
                  control={form.control}
                  name="suppliers"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value?.includes(supplier)}
                          onCheckedChange={(checked) => {
                            const currentValue = [...(field.value || [])];
                            if (checked) {
                              if (!currentValue.includes(supplier)) {
                                currentValue.push(supplier);
                              }
                            } else {
                              const index = currentValue.indexOf(supplier);
                              if (index !== -1) {
                                currentValue.splice(index, 1);
                              }
                            }
                            field.onChange(currentValue);
                          }}
                        />
                      </FormControl>
                      <FormLabel className="mt-0">{supplier}</FormLabel>
                    </FormItem>
                  )}
                />
              ))}
            </div>
          </div>

          {/* Image Upload */}
          <div className="space-y-2">
            <FormLabel>Product Image</FormLabel>
            <div className="flex items-center gap-4">
              <div 
                className={`border-2 border-dashed rounded-md p-6 flex flex-col items-center justify-center gap-2 ${
                  imagePreview ? "border-green-500" : "border-muted"
                }`}
                style={{ width: "150px", height: "150px" }}
              >
                {imagePreview ? (
                  <img 
                    src={imagePreview} 
                    alt="Product preview" 
                    className="max-h-full max-w-full object-contain" 
                  />
                ) : (
                  <>
                    <Upload className="h-10 w-10 text-muted-foreground" />
                    <p className="text-xs text-muted-foreground text-center">
                      Click to upload or drag and drop
                    </p>
                  </>
                )}
              </div>

              <div>
                <Input 
                  type="file" 
                  id="product-image" 
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageChange}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById("product-image")?.click()}
                >
                  {imagePreview ? "Change Image" : "Upload Image"}
                </Button>
                {imagePreview && (
                  <Button
                    type="button"
                    variant="ghost"
                    className="ml-2"
                    onClick={() => setImagePreview(null)}
                  >
                    Remove
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Submit buttons */}
          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="outline">Cancel</Button>
            <Button type="submit" className="bg-green-600 hover:bg-green-700">
              <Check className="mr-2 h-4 w-4" />
              Save Product
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default AddNewProductTab;
